import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { EntityMultiSelect } from "@/components/EntityMultiSelect";
import { RecurrenceFields } from "./RecurrenceFields";
import { KINDS } from "./calUtils";

const BASE_REC = { type: "yearly", month: 0, day: 1, year: null, weekday: 0, nth: 1, interval: 7, moon: 0, phase: "full", start_year: null };

export const CalEventDialog = ({ open, onOpenChange, calendar, initial, entities, onSaved, readOnly }) => {
  const editing = !!initial?.id;
  const [form, setForm] = useState({
    title: initial?.title || "", description: initial?.description || "", kind: initial?.kind || "holiday", color: initial?.color || "#F59E0B",
    duration: initial?.duration || 1, entity_ids: initial?.entity_ids || [], recurrence: { ...BASE_REC, ...(initial?.recurrence || {}) },
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = form.recurrence;
    const body = { ...form, duration: Math.max(parseInt(form.duration, 10) || 1, 1), recurrence: { ...r, day: r.day || 1, interval: r.interval || 1 } };
    try {
      if (editing) await api.patch(`/calendar-events/${initial.id}`, body);
      else await api.post(`/calendars/${calendar.id}/events`, body);
      onSaved();
      onOpenChange(false);
      toast.success("Event saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    await api.delete(`/calendar-events/${initial.id}`);
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto lf-scroll" data-testid="cal-event-dialog">
        <form onSubmit={save}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing ? (readOnly ? form.title : "Edit event") : "New recurring event"}</DialogTitle></DialogHeader>
          <fieldset disabled={readOnly} className="space-y-4 py-5">
            <div className="grid grid-cols-[1fr_56px] gap-3">
              <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={set("title")} required placeholder="Aria's birthday" data-testid="cal-event-title-input" /></div>
              <div className="space-y-1.5"><Label>Color</Label><input type="color" value={form.color} onChange={set("color")} className="h-10 w-full cursor-pointer rounded-md border border-[#2D3442] bg-transparent" data-testid="cal-event-color-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="cal-event-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Lasts (days)</Label><Input type="number" min={1} className="font-mono-lf" value={form.duration} onChange={set("duration")} data-testid="cal-event-duration-input" /></div>
            </div>
            <RecurrenceFields value={form.recurrence} onChange={(recurrence) => setForm({ ...form, recurrence })} calendar={calendar} />
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={set("description")} data-testid="cal-event-description-input" /></div>
            <div className="space-y-1.5">
              <Label>Linked entries</Label>
              <EntityMultiSelect entities={entities} value={form.entity_ids} onChange={(v) => setForm({ ...form, entity_ids: v })} testid="cal-event-entities" />
            </div>
          </fieldset>
          {!readOnly && (
            <DialogFooter className="gap-2">
              {editing && <Button type="button" variant="ghost" className="mr-auto text-red-400" onClick={remove} data-testid="cal-event-delete-button">Delete</Button>}
              <Button type="submit" disabled={busy} data-testid="cal-event-save-button">{busy ? "Saving…" : "Save event"}</Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
